import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, ArrowLeft, BriefcaseMedical, CalendarCheck, CheckCircle2, Download,
  FileText, Loader2, Plus, Save, Search, ShoppingCart, Trash2, Upload, FileSpreadsheet,
} from "lucide-react";

/**
 * Le cassette di primo soccorso, una per mezzo.
 *
 * Il foglio Excel aziendale teneva una riga per targa con la scadenza di ogni
 * prodotto. Qui e' la stessa cosa, ma i due conti che contavano davvero - qual
 * e' il primo prodotto a scadere e quando tocca il prossimo controllo - li fa
 * l'app invece di una formula che qualcuno deve ricordarsi di trascinare.
 */

const COLORE = {
  ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
  in_scadenza: "bg-amber-100 text-amber-900 border-amber-200",
  da_reintegrare: "bg-red-100 text-red-800 border-red-200",
  scaduto: "bg-red-100 text-red-800 border-red-200",
  non_presente: "bg-slate-200 text-slate-700 border-slate-300",
  mai_controllata: "bg-sky-100 text-sky-800 border-sky-200",
  controllo_scaduto: "bg-orange-100 text-orange-900 border-orange-200",
};

/** Il registro SC PR-08.3.2 aggiornato a questo momento. */
async function scaricaRegistro() {
  try {
    const { data } = await api.get("/cassette/registro/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = "SC-PR-08.3.2 Registro cassette.pdf";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch { toast.error("Errore nella generazione del registro"); }
}

/** Il registro di controllo di una cassetta sola: quello del mezzo. */
async function scaricaRegistroCassetta(id, nome) {
  try {
    const { data } = await api.get(`/cassette/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `SC-PR-08.3.2 Cassetta ${(nome || "").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch { toast.error("Errore nella generazione del registro"); }
}

function dataIt(iso) {
  if (!iso) return "";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

function meseIt(iso) {
  if (!iso) return "";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[1]}/${p[0]}` : String(iso);
}

export default function Cassette() {
  const { user } = useAuth();
  const puoScrivere = ["admin", "amministrativo"].includes(user?.role);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [cerca, setCerca] = useState("");
  const [filtro, setFiltro] = useState("");
  const [aperta, setAperta] = useState(null);
  const [importa, setImporta] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const { data } = await api.get("/cassette");
      setDati(data);
    } catch {
      toast.error("Errore nel caricamento delle cassette");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  // La cassetta che non c'e' piu' - persa, spostata su un altro mezzo -
  // si toglie dall'elenco; se torna, la si rimette dalla scheda del mezzo.
  const elimina = async (c) => {
    if (!window.confirm(`Eliminare la cassetta di ${c.nome}?

Se la cassetta è stata spostata su un altro mezzo, registrala da quel mezzo.`)) return;
    try {
      await api.delete(`/cassette/${c.id}`);
      toast.success("Cassetta eliminata");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const scarica = scaricaRegistro;

  const [ordini, setOrdini] = useState(false);

  if (aperta) {
    return <Dettaglio id={aperta} onIndietro={() => { setAperta(null); carica(); }} />;
  }
  if (ordini) {
    return <DaOrdinare onIndietro={() => setOrdini(false)} onApri={(id) => { setOrdini(false); setAperta(id); }} />;
  }

  const q = cerca.trim().toLowerCase();
  const righe = (dati?.cassette || []).filter(
    (c) => (!filtro || c.stato === filtro)
           && (!q || (c.nome || "").toLowerCase().includes(q)));
  const t = dati?.totali || {};

  return (
    <div className="space-y-6" data-testid="cassette-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Mezzi e attrezzature</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            Cassette di primo soccorso
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Una per mezzo, più quelle di sede. Si controllano ogni sei mesi.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setOrdini(true)} className="h-11"
                  data-testid="cassette-da-ordinare">
            <ShoppingCart className="w-4 h-4 mr-2" /> Cosa ordinare
          </Button>
          {puoScrivere && (
            <Button variant="outline" onClick={() => setImporta(true)} className="h-11"
                    data-testid="importa-cassette">
              <Upload className="w-4 h-4 mr-2" /> Importa dal foglio
            </Button>
          )}
          <Button onClick={scarica}
                  className="bg-orange-500 hover:bg-orange-600 text-white h-11">
            <Download className="w-4 h-4 mr-2" /> Registro in PDF
          </Button>
        </div>
      </div>

      {carico && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
        </p>
      )}

      {!carico && dati && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[["", "In tutto", t.quante, "text-slate-800"],
              ["da_reintegrare", "Da sistemare", t.da_sistemare, "text-red-600"],
              ["controllo_scaduto", "Controllo scaduto", t.controllo_scaduto, "text-orange-600"],
              ["in_scadenza", "In scadenza", t.in_scadenza, "text-amber-600"],
              ["non_presente", "Senza cassetta", t.non_presenti, "text-slate-500"]]
              .map(([codice, nome, quante, colore]) => (
              <button key={nome} type="button"
                      onClick={() => setFiltro(filtro === codice ? "" : codice)}
                      data-testid={`filtro-${codice || "tutte"}`}
                      className={`text-left border rounded-lg p-3 transition-colors ${
                        filtro === codice && codice
                          ? "border-[#0067b2] bg-sky-50"
                          : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className={`font-display text-3xl font-black tabular-nums ${colore}`}>
                  {quante ?? 0}
                </div>
                <div className="text-xs text-slate-500">{nome}</div>
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                   placeholder="Cerca una targa" className="pl-9 h-11" />
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {righe.map((c) => (
              <button key={c.id} type="button" onClick={() => setAperta(c.id)}
                      data-testid={`cassetta-${c.id}`}
                      className="text-left border border-slate-200 bg-white rounded-lg p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold leading-tight truncate flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{c.nome}</span>
                    {puoScrivere && (
                      <span role="button" tabIndex={0} title="Elimina la cassetta"
                            data-testid={`elimina-cassetta-${c.id}`}
                            onClick={(e) => { e.stopPropagation(); elimina(c); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); elimina(c); } }}
                            className="text-slate-300 hover:text-red-600 shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 ${
                    COLORE[c.stato] || COLORE.ok}`}>
                    {c.stato_nome}
                  </span>
                </div>
                {!c.non_presente && (
                  <>
                    <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                      {c.da_reintegrare > 0 && (
                        <div className="text-red-700">
                          {c.da_reintegrare} da reintegrare
                        </div>
                      )}
                      {c.scaduti > 0 && (
                        <div className="text-red-700">{c.scaduti} scaduti</div>
                      )}
                      {c.in_scadenza > 0 && (
                        <div className="text-amber-700">
                          {c.in_scadenza} in scadenza
                        </div>
                      )}
                      {c.prima_scadenza && (
                        <div>Prima scadenza: {meseIt(c.prima_scadenza)}</div>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                      <CalendarCheck className="w-3 h-3" />
                      {c.prossimo_controllo
                        ? `Prossimo controllo ${dataIt(c.prossimo_controllo)}`
                        : "Mai controllata"}
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>
          {righe.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
              Nessuna cassetta con questo filtro.
            </p>
          )}
        </>
      )}

      {importa && (
        <DialogImporta onChiudi={() => setImporta(false)}
                       onFatto={() => { setImporta(false); carica(); }} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function Dettaglio({ id, onIndietro }) {
  const [c, setC] = useState(null);
  const [bozza, setBozza] = useState({});
  const [data, setData] = useState("");
  const [note, setNote] = useState("");
  const [salvo, setSalvo] = useState(false);
  // Cosa ha prodotto l'ultimo salvataggio: la conferma da mostrare sotto il
  // pulsante, con la registrazione sulla scheda dei sistemi di gestione.
  const [fatto, setFatto] = useState(null);

  const carica = useCallback(async () => {
    const { data: d } = await api.get(`/cassette/${id}`);
    setC(d);
    const b = {};
    for (const p of d.prodotti) {
      b[p.codice] = { scadenza: p.scadenza || "", da_reintegrare: p.da_reintegrare,
                      senza_scadenza: p.senza_scadenza };
    }
    setBozza(b);
    setData(d.ultimo_controllo || "");
    setNote(d.note || "");
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  const metti = async () => {
    setSalvo(true);
    try {
      const { data: d } = await api.put(`/cassette/${id}`, { non_presente: false });
      setC(d);
      toast.success("Cassetta aggiunta: ora si compilano le scadenze");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  const togli = async () => {
    if (!window.confirm("Segnare che su questo mezzo la cassetta non c'è?")) return;
    setSalvo(true);
    try {
      const { data: d } = await api.put(`/cassette/${id}`, { non_presente: true });
      setC(d);
      toast.success("Segnata come non presente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  const salva = async () => {
    setSalvo(true);
    try {
      const { data: d } = await api.put(`/cassette/${id}`, {
        prodotti: bozza, ultimo_controllo: data || null, note,
        non_presente: false,
      });
      setC(d);
      setFatto({ data: d.ultimo_controllo, sistemi: d.registrato_nei_sistemi || null });
      toast.success("Controllo registrato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  if (!c) {
    return (
      <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
      </p>
    );
  }

  const cambia = (codice, campo, valore) => setBozza((p) => ({
    ...p,
    [codice]: campo === "da_reintegrare"
      ? { ...p[codice], da_reintegrare: valore, scadenza: valore ? "" : p[codice].scadenza }
      : { ...p[codice], [campo]: valore, da_reintegrare: false },
  }));

  return (
    <div className="space-y-5" data-testid="dettaglio-cassetta">
      <div>
        <button type="button" onClick={onIndietro}
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1">
          <ArrowLeft className="w-3 h-3" /> Tutte le cassette
        </button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter">
              {c.nome}
            </h1>
            <p className="text-sm text-slate-500">
              {c.tipo === "sede" ? "Cassetta di sede · allegato 1"
                                 : "Pacchetto di medicazione · allegato 2"}
              {" · "}{c.quanti_prodotti} prodotti
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Il foglio da mettere nel portadocumenti: sta qui sempre, non
                solo dopo aver registrato un controllo. */}
            <Button variant="outline" size="sm" className="h-9"
                    onClick={() => scaricaRegistroCassetta(c.id, c.nome)}
                    data-testid="foglio-cassetta-scheda"
                    title="Contenuto, scadenze, controlli fatti e righe da firmare">
              <FileText className="w-4 h-4 mr-2" /> Foglio della cassetta
            </Button>
            <span className={`text-xs uppercase tracking-wide border rounded-full px-3 py-1.5 ${
              COLORE[c.stato] || COLORE.ok}`}>
              {c.stato_nome}
            </span>
          </div>
        </div>
      </div>

      {c.non_presente && (
        <Card className="border-2 border-slate-300 bg-slate-50">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">
                Su questo mezzo la cassetta non c&apos;è
              </span>
              <span className="block text-sm text-slate-500">
                Se è stata messa a bordo, aggiungila qui: da quel momento entra nel
                giro dei controlli semestrali.
              </span>
            </span>
            <Button onClick={metti} disabled={salvo}
                    data-testid="metti-cassetta"
                    className="bg-orange-500 hover:bg-orange-600 text-white h-11">
              <Plus className="w-4 h-4 mr-2" /> Aggiungi la cassetta
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Data del controllo
            </Label>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)}
                   data-testid="data-controllo"
                   className="w-full h-11 mt-1 border border-slate-200 rounded-lg px-3 text-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Prossimo controllo
            </Label>
            <div className="h-11 mt-1 flex items-center text-sm">
              {c.prossimo_controllo ? dataIt(c.prossimo_controllo) : "—"}
              <span className="text-slate-400 ml-2">(sei mesi dopo)</span>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   className="h-11 mt-1" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-1">
          <h2 className="font-semibold mb-2 flex items-center gap-2">
            <BriefcaseMedical className="w-4 h-4 text-[#0067b2]" /> Contenuto
          </h2>
          {/* Sul telefono: il nome sopra, sotto scadenza, spunta e stato in una
              riga sola che sta dentro lo schermo. Da tablet in su tutto di
              fianco, come prima. */}
          {c.prodotti.map((p) => (
            <div key={p.codice}
                 className="py-2 border-b border-slate-100 last:border-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <span className="min-w-0 sm:flex-1">
                <span className="block text-sm">{p.nome}</span>
                <span className="block text-xs text-slate-400">
                  quantità minima {p.quantita}
                </span>
              </span>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <input type="month"
                       value={(bozza[p.codice]?.scadenza || "").slice(0, 7)}
                       data-testid={`scadenza-${p.codice}`}
                       onChange={(e) => cambia(p.codice, "scadenza",
                                               e.target.value ? `${e.target.value}-01` : "")}
                       className="h-10 flex-1 min-w-0 sm:flex-none sm:w-36 border border-slate-200 rounded-lg px-2 text-sm" />
                <label className="flex items-center gap-1.5 text-xs cursor-pointer shrink-0">
                  <input type="checkbox" checked={Boolean(bozza[p.codice]?.da_reintegrare)}
                         onChange={(e) => cambia(p.codice, "da_reintegrare", e.target.checked)}
                         className="w-4 h-4 accent-red-500" />
                  <span className="hidden sm:inline">da reintegrare</span>
                  <span className="sm:hidden">manca</span>
                </label>
                <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 w-20 sm:w-28 text-center shrink-0 ${
                  COLORE[p.stato] || COLORE.ok}`}>
                  {p.stato_nome}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {(c.controlli || []).length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <h2 className="font-display font-bold text-sm flex items-center gap-2 mb-2">
              <CalendarCheck className="w-4 h-4 text-orange-500" /> Controlli di questo mezzo
            </h2>
            <div className="space-y-1">
              {(c.controlli || []).slice().reverse().slice(0, 8).map((s) => (
                <div key={s.data} className="flex items-center gap-2 text-xs border border-slate-200 rounded px-2 py-1">
                  <span className="font-semibold w-20 flex-shrink-0">{dataIt(s.data)}</span>
                  <span className="text-slate-500 min-w-0 flex-1 truncate">{s.da || "—"}</span>
                  <span className={`uppercase tracking-wide border rounded px-1.5 py-0.5 ${
                    s.esito === "conforme" ? COLORE.ok : COLORE.da_reintegrare}`}>
                    {s.esito === "conforme" ? "conforme" : "anomalia"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Button onClick={salva} disabled={salvo} data-testid="salva-cassetta"
              className="bg-orange-500 hover:bg-orange-600 text-white h-12 w-full">
        {salvo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
               : <Save className="w-4 h-4 mr-2" />}
        Registra il controllo
      </Button>
      {fatto && (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="controllo-fatto">
          <div className="flex items-start gap-2 text-sm text-emerald-900">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">
                Controllo registrato{fatto.data ? ` il ${dataIt(fatto.data)}` : ""}.
              </div>
              {fatto.sistemi ? (
                <div className="text-xs mt-0.5">
                  Segnato anche sulla scheda «{fatto.sistemi.titolo}» dei sistemi di gestione:
                  {" "}{fatto.sistemi.cassette} cassett{fatto.sistemi.cassette === 1 ? "a" : "e"} in questa giornata,
                  esito {fatto.sistemi.esito === "conforme" ? "conforme" : "con anomalie"}.
                </div>
              ) : (
                <div className="text-xs mt-0.5">
                  Il registro qui sotto è aggiornato a questo controllo.
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => scaricaRegistroCassetta(c.id, c.nome)}
                    className="h-9 bg-white" data-testid="registro-aggiornato">
              <FileText className="w-4 h-4 mr-2" /> Registro di questo mezzo (PDF)
            </Button>
            <Button variant="ghost" onClick={scaricaRegistro} className="h-9 text-slate-600">
              <Download className="w-4 h-4 mr-2" /> Registro di tutte le cassette
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pb-8 flex-wrap">
        <p className="text-xs text-slate-400 max-w-md">
          La data del controllo fa ripartire i sei mesi. Un prodotto senza scadenza —
          le forbici, i sacchetti — si lascia in bianco.
        </p>
        {!c.non_presente && (
          <Button variant="ghost" onClick={togli} disabled={salvo}
                  className="text-slate-500 hover:text-slate-800 h-9">
            Su questo mezzo la cassetta non c&apos;è
          </Button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function DialogImporta({ onChiudi, onFatto }) {
  const [file, setFile] = useState(null);
  const [visto, setVisto] = useState(null);
  const [lavoro, setLavoro] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) return;
    setLavoro(true);
    try {
      const modulo = new FormData();
      modulo.append("file", file);
      modulo.append("solo_verifica", soloVerifica ? "true" : "false");
      const { data } = await api.post("/cassette/importa", modulo);
      if (soloVerifica) setVisto(data);
      else {
        toast.success(`${data.create} create, ${data.aggiornate} aggiornate`);
        onFatto();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella lettura del file");
    } finally { setLavoro(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importa il foglio delle cassette</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Il file «CONTROLLI CASSETTE DI PRIMO SOCCORSO.xlsx». Si legge il foglio
            con le targhe: una riga per mezzo, la scadenza di ogni prodotto e la data
            dell&apos;ultimo controllo.
          </p>
          <Input type="file" accept=".xlsx,.xlsm"
                 data-testid="file-cassette"
                 onChange={(e) => { setFile(e.target.files[0]); setVisto(null); }} />

          {!visto && (
            <Button onClick={() => manda(true)} disabled={!file || lavoro}
                    className="w-full h-11" variant="outline">
              {lavoro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guarda cosa cambierebbe
            </Button>
          )}

          {visto && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Righe nel file", visto.righe_nel_file],
                  ["Da creare", visto.da_creare],
                  ["Da aggiornare", visto.da_aggiornare]].map(([n, q]) => (
                  <div key={n} className="border border-slate-200 rounded-lg p-3">
                    <div className="font-display text-2xl font-black tabular-nums">{q}</div>
                    <div className="text-xs text-slate-500">{n}</div>
                  </div>
                ))}
              </div>

              {visto.targhe_senza_mezzo.length > 0 && (
                <div className="text-xs border border-amber-200 bg-amber-50 rounded-lg p-3 text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  {visto.targhe_senza_mezzo.length} targhe del foglio non sono in
                  anagrafica e verranno saltate: {visto.targhe_senza_mezzo.join(", ")}
                </div>
              )}
              {visto.mezzi_non_nel_file.length > 0 && (
                <div className="text-xs border border-sky-200 bg-sky-50 rounded-lg p-3 text-sky-900">
                  {visto.mezzi_non_nel_file.length} mezzi in anagrafica non sono nel
                  foglio: gli si crea la scheda vuota, da riempire.{" "}
                  {visto.mezzi_non_nel_file.join(", ")}
                </div>
              )}

              <Button onClick={() => manda(false)} disabled={lavoro}
                      data-testid="conferma-import-cassette"
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white">
                {lavoro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Importa davvero
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// La lista dei reintegri: prodotto per prodotto, in quante cassette e'
// scaduto, in scadenza o mancante, e su quali mezzi. E' quello che serve
// per fare l'ordine al fornitore, senza aprire le cassette una per una.
function DaOrdinare({ onIndietro, onApri }) {
  const [dati, setDati] = useState(null);
  const [filtro, setFiltro] = useState("tutti");   // tutti | scaduti | in_scadenza
  const [espanso, setEspanso] = useState(null);

  useEffect(() => {
    api.get("/cassette/da-ordinare").then(({ data }) => setDati(data))
      .catch(() => { toast.error("Errore nel caricamento"); setDati({ righe: [], totali: {} }); });
  }, []);

  const scaricaExcel = async () => {
    try {
      const { data } = await api.get("/cassette/da-ordinare/excel", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "Cassette da ordinare.xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del file"); }
  };

  const NOME = { scaduto: "scaduto", in_scadenza: "in scadenza", da_reintegrare: "da reintegrare" };
  const COL = { scaduto: "bg-red-100 text-red-700", in_scadenza: "bg-amber-100 text-amber-700", da_reintegrare: "bg-red-100 text-red-700" };
  const righe = (dati?.righe || []).map((r) => ({
    ...r,
    cassette: r.cassette.filter((c) => filtro === "tutti"
      || (filtro === "scaduti" && c.stato !== "in_scadenza")
      || (filtro === "in_scadenza" && c.stato === "in_scadenza")),
  })).filter((r) => r.cassette.length > 0);
  const tot = dati?.totali || {};

  return (
    <div className="space-y-4" data-testid="da-ordinare">
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onIndietro}>
          <ArrowLeft className="w-3 h-3 mr-1" /> Cassette
        </Button>
        <div className="flex-1 min-w-[220px]">
          <h2 className="font-display text-2xl font-black tracking-tight">Cosa ordinare</h2>
          <p className="text-sm text-slate-500">
            Prodotto per prodotto, su tutte le cassette: cosa è scaduto, cosa scade entro il preavviso, cosa manca.
          </p>
        </div>
        <Button variant="outline" onClick={scaricaExcel} data-testid="da-ordinare-excel">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel per il fornitore
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["tutti", `Tutto (${(tot.scaduti || 0) + (tot.in_scadenza || 0)})`],
          ["scaduti", `Scaduti e mancanti (${tot.scaduti || 0})`],
          ["in_scadenza", `In scadenza (${tot.in_scadenza || 0})`]].map(([codice, nome]) => (
          <button key={codice} type="button" onClick={() => setFiltro(codice)}
                  data-testid={`ordinare-${codice}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    filtro === codice ? "bg-[#0067b2] text-white border-[#0067b2]" : "bg-white text-slate-600 border-slate-200"}`}>
            {nome}
          </button>
        ))}
        {dati && <span className="text-xs text-slate-400 self-center ml-auto">{tot.pezzi || 0} pezzi in tutto</span>}
      </div>

      {!dati && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {dati && righe.length === 0 && (
        <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          Niente da ordinare con questo filtro.
        </p>
      )}
      <div className="space-y-1.5">
        {righe.map((r) => {
          const aperto = espanso === r.codice;
          const pezzi = r.cassette.length * r.per_cassetta;
          return (
            <div key={r.codice} className="border border-slate-200 rounded-lg bg-white">
              <button type="button" onClick={() => setEspanso(aperto ? null : r.codice)}
                      className="w-full text-left px-3 py-2 flex items-center gap-3">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold truncate">{r.nome}</span>
                  <span className="block text-xs text-slate-500">
                    {r.cassette.length} cassett{r.cassette.length === 1 ? "a" : "e"} · {pezzi} pezz{pezzi === 1 ? "o" : "i"} da ordinare
                  </span>
                </span>
                {r.cassette.some((c) => c.stato !== "in_scadenza") && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {r.cassette.filter((c) => c.stato !== "in_scadenza").length} scadut./mancanti
                  </span>
                )}
                {r.cassette.some((c) => c.stato === "in_scadenza") && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {r.cassette.filter((c) => c.stato === "in_scadenza").length} in scadenza
                  </span>
                )}
              </button>
              {aperto && (
                <div className="px-3 pb-2 divide-y divide-slate-100">
                  {r.cassette.map((c) => (
                    <div key={c.id} className="py-1.5 flex items-center gap-2 text-sm">
                      <button type="button" onClick={() => onApri(c.id)} className="flex-1 min-w-0 text-left truncate hover:underline">
                        {c.nome || c.targa}
                      </button>
                      <span className="text-xs text-slate-500 tabular-nums">{c.scadenza ? dataIt(c.scadenza) : ""}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${COL[c.stato]}`}>{NOME[c.stato]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

